import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AlertTriangle, Clock, CalendarClock, Building2, Loader2, Mail } from "lucide-react";

function StatCard({ icon: Icon, label, value, color, testid }) {
  return (
    <Card className="flex items-center gap-4 p-5" data-testid={testid}>
      <div className={`flex h-12 w-12 items-center justify-center rounded-xl ${color}`}><Icon className="h-6 w-6" /></div>
      <div>
        <div className="font-heading text-2xl font-bold text-[#0a192f]">{value}</div>
        <div className="text-sm text-slate-500">{label}</div>
      </div>
    </Card>
  );
}

export default function AdminDashboard() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [companyFilter, setCompanyFilter] = useState("all");
  const [stateFilter, setStateFilter] = useState("all");

  useEffect(() => { api.get("/dashboard/admin").then((r) => setData(r.data)); }, []);

  const companies = useMemo(() => {
    if (!data) return [];
    const m = {};
    data.rows.forEach((r) => { m[r.company_id] = r.company_name; });
    return Object.entries(m).map(([id, name]) => ({ id, name }));
  }, [data]);

  const rows = useMemo(() => {
    if (!data) return [];
    return data.rows.filter((r) =>
      (companyFilter === "all" || r.company_id === companyFilter) &&
      (stateFilter === "all" || r.state === stateFilter));
  }, [data, companyFilter, stateFilter]);

  if (!data) return <div className="flex justify-center py-20"><Loader2 className="h-7 w-7 animate-spin text-emerald-600" /></div>;

  return (
    <div className="fade-in space-y-6">
      <div>
        <h1 className="font-heading text-3xl font-extrabold tracking-tight text-[#0a192f]">Yönetim Paneli</h1>
        <p className="mt-1 text-slate-500">Tüm firmaların yaklaşan ve süresi dolan belge tarihleri</p>
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard icon={AlertTriangle} label="Süresi Doldu" value={data.summary.expired} color="bg-red-100 text-red-700" testid="stat-expired" />
        <StatCard icon={Clock} label="Kritik" value={data.summary.critical} color="bg-orange-100 text-orange-700" testid="stat-critical" />
        <StatCard icon={CalendarClock} label="Yaklaşan" value={data.summary.upcoming} color="bg-amber-100 text-amber-700" testid="stat-upcoming" />
        <StatCard icon={Building2} label="Toplam Firma" value={data.total_companies} color="bg-emerald-100 text-emerald-700" testid="stat-companies" />
      </div>

      <Card className="overflow-hidden">
        <div className="flex flex-wrap items-center gap-3 border-b border-slate-100 p-4">
          <span className="text-sm font-semibold text-slate-600">Filtrele:</span>
          <Select value={companyFilter} onValueChange={setCompanyFilter}>
            <SelectTrigger className="w-52" data-testid="filter-company"><SelectValue placeholder="Firma" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tüm Firmalar</SelectItem>
              {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={stateFilter} onValueChange={setStateFilter}>
            <SelectTrigger className="w-44" data-testid="filter-state"><SelectValue placeholder="Durum" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tüm Durumlar</SelectItem>
              <SelectItem value="expired">Süresi Doldu</SelectItem>
              <SelectItem value="critical">Kritik</SelectItem>
              <SelectItem value="upcoming">Yaklaşan</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {rows.length === 0 ? (
          <div className="p-12 text-center text-slate-400" data-testid="dashboard-empty">
            Filtreye uygun yaklaşan veya süresi dolmuş belge bulunmuyor.
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Firma</TableHead>
                <TableHead>Belge / Alt Tür</TableHead>
                <TableHead>Belge Türü</TableHead>
                <TableHead>Son Tarih</TableHead>
                <TableHead>Durum</TableHead>
                <TableHead>E-posta</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.record_id} data-testid={`deadline-row-${r.record_id}`}
                  className="cursor-pointer" onClick={() => nav(`/admin/firmalar/${r.company_id}`)}>
                  <TableCell className="font-medium text-[#0a192f]">{r.company_name}</TableCell>
                  <TableCell>{r.name}{r.subtype ? <span className="ml-1 text-xs text-slate-400">({r.subtype})</span> : ""}</TableCell>
                  <TableCell className="text-sm text-slate-500">{r.type_label}</TableCell>
                  <TableCell className="text-sm">{r.deadline}</TableCell>
                  <TableCell><StatusBadge status={{ state: r.state, days_left: r.days_left }} /></TableCell>
                  <TableCell>
                    {r.email_status ? (
                      <span className="inline-flex items-center gap-1 text-xs text-emerald-700">
                        <Mail className="h-3.5 w-3.5" /> {r.email_status === "captured" ? "Yakalandı" : r.email_status === "sent" ? "Gönderildi" : r.email_status}
                      </span>
                    ) : <span className="text-xs text-slate-400">—</span>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>
    </div>
  );
}
